// Approvals — owner-side inbox for EXPLICIT subscription-approval requests.
// Strict LIST layout: one line per pending request, newest → oldest. Each line
// shows the requester's username + when they asked, with Approve (confirm
// dialog) and Deny (no confirm) buttons. Mirrors the 3s badge/banner poll so the
// list stays fresh while open.
import { h, toast, confirmDialog } from '../components/dom.js';
import { renderShell, pageHead, portalFooter } from '../components/shell.js';
import { api } from '../api/client.js';
import type { ApprovalRequest } from '../api/types.js';
import { relativeTime } from './approvals-format.js';
import { dateTimeEl } from '../lib/timezone.js';
export { relativeTime } from './approvals-format.js';
import { mergeContactsByOwner, type MergedContact } from './merge.js';
import { openContactCardModal } from './contact-card-modal.js';
import type { Contact } from '../api/types.js';

const POLL_MS = 3000;

// relativeTime renders a compact "3m ago" style stamp; exported for unit tests.
export function requesterName(r: ApprovalRequest): string {
  return r.subscriber_handle || r.subscriber_name || r.subscriber_id;
}

// The counterparty's first + last name (if provided), for the muted subline.
export function requesterFullName(r: ApprovalRequest): string {
  return [r.subscriber_given_name, r.subscriber_family_name]
    .map((s) => (s || '').trim())
    .filter(Boolean)
    .join(' ');
}

export async function renderApprovals(): Promise<void> {
  const main = renderShell('/approvals');
  main.append(pageHead('Approvals', 'People asking to subscribe to a profile you share explicitly.'));

  const list = h('div', { class: 'card', style: 'padding:0;overflow:hidden' });
  main.append(list, portalFooter());

  let poll: ReturnType<typeof setInterval> | null = null;
  const stop = (): void => { if (poll) { clearInterval(poll); poll = null; } };
  // Owner-indexed merged contacts, so an approval preview can show the person's
  // full card IF they're already a saved contact. Refreshed alongside each poll.
  let contactByOwner = new Map<string, MergedContact>();
  const loadContacts = async (): Promise<void> => {
    try {
      const res = await api.contacts().catch(() => ({ data: [] as Contact[] }));
      contactByOwner = new Map(mergeContactsByOwner(res.data).map((m) => [m.ownerId, m]));
    } catch { /* non-fatal: preview just falls back to a minimal card */ }
  };
  // Stop polling when we navigate away (view container detached).
  const alive = (): boolean => document.body.contains(list);

  const row = (r: ApprovalRequest): HTMLElement => {
    const name = requesterName(r);
    const fullName = requesterFullName(r);
    const line = h('div', {
      class: 'approval-row',
      style: 'display:flex;align-items:center;gap:12px;padding:12px 16px;border-bottom:1px solid var(--border,#eee)',
    });
    const info = h('div', { style: 'flex:1;min-width:0' },
      (() => {
        const nameEl = h('a', { href: '#', style: 'font-weight:700;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:block;text-decoration:none' }, '@' + name);
        // Preview the person's contact card. The "Show on My Contacts" link inside
        // the popup is auto-hidden when they aren't a saved contact yet.
        nameEl.addEventListener('click', (e) => {
          e.preventDefault();
          openContactCardModal({ contact: contactByOwner.get(r.subscriber_id || ''), username: name, showNavToContact: true });
        });
        return nameEl;
      })(),
      ...(fullName
        ? [h('div', { style: 'font-size:.9rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap' }, fullName)]
        : []),
      h('div', { class: 'muted', style: 'font-size:.85rem' },
        'requested ', relativeTime(r.requested_at), ' · ', dateTimeEl(r.requested_at),
        r.profile_name ? ' · ' + r.profile_name : ''),
    );
    const approve = h('button', { class: 'btn btn-primary btn-sm' }, 'Approve') as HTMLButtonElement;
    const deny = h('button', { class: 'btn btn-sm' }, 'Deny') as HTMLButtonElement;
    approve.addEventListener('click', async () => {
      if (!confirmDialog(`Approve @${name} as a subscriber? They'll receive your profile fields.`)) return;
      approve.disabled = deny.disabled = true;
      try {
        await api.approveRequest(r.id);
        toast(`Approved @${name}`, 'ok');
        line.remove();
        if (!list.querySelector('.approval-row')) renderEmpty();
      } catch (e) {
        toast((e as Error).message, 'danger');
        approve.disabled = deny.disabled = false;
      }
    });
    deny.addEventListener('click', async () => {
      approve.disabled = deny.disabled = true;
      try {
        await api.denyRequest(r.id);
        toast(`Denied @${name}`, 'warn');
        line.remove();
        if (!list.querySelector('.approval-row')) renderEmpty();
      } catch (e) {
        toast((e as Error).message, 'danger');
        approve.disabled = deny.disabled = false;
      }
    });
    line.append(info, h('div', { class: 'row', style: 'gap:8px' }, approve, deny));
    return line;
  };

  const renderEmpty = (): void => {
    list.replaceChildren(h('div', { class: 'muted', style: 'padding:28px 16px;text-align:center' },
      'No pending approvals. New subscription requests will appear here.'));
  };

  const draw = (reqs: ApprovalRequest[]): void => {
    if (reqs.length === 0) { renderEmpty(); return; }
    // Server returns newest→oldest; render in that order.
    list.replaceChildren(...reqs.map(row));
  };

  const load = async (): Promise<void> => {
    if (!alive()) { stop(); return; }
    try {
      const res = await api.approvals();
      if (alive()) draw(res.requests || []);
    } catch { /* transient; keep current */ }
  };

  await loadContacts();
  await load();
  poll = setInterval(() => void load(), POLL_MS);
}
